import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";

import { RootState } from "typedefs";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { Loading } from "app/components/Wrappers";
import PackageCard from "./PackageCard";
import ContactCoachModal from "app/components/PublicProfiles/Coach/ContactCoachModal";
import PackageDetailsModal from "app/routes/Billing/PackageDetailsModal";
import {
  canPurchaseOnline,
  isAlreadySubscribed,
  packageCtaKey,
} from "app/components/PublicProfiles/services/purchasability";
import { Package } from "app/components/PublicProfiles/services/api";
import EventsClinics from "app/components/PublicProfiles/Coach/EventsClinics";
import { cls } from "app/utils";
import {
  hasFacilityBanner,
  hasFacilityLogo,
} from "app/components/PublicProfiles/Facility/facilityBranding";
import CoachAvatar from "../AllCoachesPublicSession/_components/CoachAvatar";
import {
  coachMetaLine,
  orderRatesForAthlete,
} from "../AllCoachesPublicSession/_components/coachMeta";
import { nextSlotLabel } from "../AllCoachesPublicSession/_components/coachNextSlot";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { timePattern } from "app/utils/timePattern";
import useRateNextSlots from "../AllCoachesPublicSession/_components/useRateNextSlots";
import {
  getApplicablePackageCredit,
  rateRequiresPackage,
} from "app/utils/creditBundles";
import { setBookSession } from "app/actions/scheduling/bookSession";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import usePackageCredits from "../hooks/usePackageCredits";
import { useTimeZonesModel } from "../indexModel";
import { TimeZoneModel } from "app/models/scheduling/TimeZoneModel";
import SessionCard from "./SessionCard";
import CoachAbout from "./CoachAbout";
import CoachTabs, {
  COACH_TABS,
  CoachTab,
  coachTabPath,
} from "./CoachTabs";
import useCoachOrgProfile from "../hooks/useCoachOrgProfile";
import { safeExternalUrl } from "app/utils/externalUrl";
import "app/components/PublicProfiles/styles.scss";

import styles from "./styles.module.scss";
import useCoachDetail from "./useCoachDetail";

/**
 * A coach's page inside the app (frame 43592:26426): their cover photo, a card
 * carrying who they are and how to reach them, their bio, then the four tabs —
 * Overview, Private Sessions, Packages & Subscriptions, Events & Clinics.
 *
 * The Overview is a summary, not a third place to read the whole list: each
 * section shows as much as the frame draws a row of and hands the rest to its own
 * tab through "View All". So the page opens on something the length of a screen
 * however many options a coach has.
 *
 * Private Sessions and Packages & Subscriptions are this page's own cards
 * (SessionCard, PackageCard) — the public profile's versions are laid out for
 * that page, not this frame. Clinics & Events is the public profile's component,
 * so that section alone carries `.public-profiles-wrapper`, the class its `pub-*`
 * rules are scoped under. Keeping that class off the rest of the page is what
 * stops those rules reaching cards they were not written for.
 */
/**
 * How much of each section the Overview shows: the rows the frame draws — two by
 * two for the session cards, one row of three for packages and for clinics.
 * Anything past that lives on the section's own tab, behind "View All".
 */
const OVERVIEW_SESSIONS = 4;
const OVERVIEW_PACKAGES = 3;
const OVERVIEW_EVENTS = 3;

interface Props {
  /**
   * CFTB-3430: the public flow mounts this page without a route param — its coach
   * comes from the `?coach=<calendar uuid>` link, resolved to an external id by
   * the page that owns that URL.
   */
  coachExternalId?: string;
  /** Where booking a session goes. Defaults to the in-app booking route. */
  bookRoute?: (rate: LessonRateModel) => string;
  /**
   * The sessions to list, when the caller already holds them.
   *
   * The public flow does. Its own request — `/coach_profiles/:calendar_uuid` — carries
   * the coach's rates, and it must not depend on this page's `landing_page_coach`
   * call for them: that endpoint answers 403 unless the coach has switched their
   * landing page on, which is a different flag from the public calendar this link
   * belongs to. A coach with a calendar but no landing page would otherwise get an
   * empty list where their sessions should be.
   */
  lessonRates?: LessonRateModel[];
  /**
   * The facility's logo and name, with the back arrow. Off in the public flow,
   * where the header strip above already carries the facility and a shared link
   * has nothing to go back to.
   */
  showFacilityRow?: boolean;
  /**
   * Which scheduling surface the availability behind the cards' "Next:" line is
   * asked for — the public endpoints, which know a coach by their calendar uuid,
   * or the authenticated ones, which know them by their external id. Stated by the
   * caller rather than guessed from the route, which is what the facility page's
   * own `isPublicPage` does.
   */
  isPublicPage?: boolean;
}

const CoachDetail: React.FC<Props> = ({
  coachExternalId: coachExternalIdProp,
  bookRoute,
  lessonRates: lessonRatesProp,
  showFacilityRow = true,
  isPublicPage = false,
}) => {
  const { t } = useTranslation();
  const { preferences } = useCalendarPreferences();
  const history = useHistory();
  const dispatch = useDispatch();
  const routeParams = useParams<{ coachExternalId?: string; tab?: string }>();
  const coachExternalId = coachExternalIdProp || routeParams.coachExternalId;

  /**
   * The tabs are part of the URL where this page *is* the URL, and local state
   * where it is a section of somebody else's — the facility booking flow mounts
   * it under `/public/facility_availability`, which owns its own query string.
   */
  const tabBasePath = routeParams.coachExternalId
    ? `/sessions/coach/${routeParams.coachExternalId}`
    : undefined;
  const [localTab, setLocalTab] = useState<CoachTab>("overview");
  // Matched rather than cast: the route pattern already limits the segment, and
  // this way a URL that somehow carries anything else opens the Overview instead
  // of a page with no tab selected and nothing under it.
  const routedTab = COACH_TABS.find((tab) => tab === routeParams.tab);
  const activeTab: CoachTab = tabBasePath ? routedTab || "overview" : localTab;
  const goToTab = (tab: CoachTab) => {
    if (tabBasePath) {
      // Same `replace` the tabs themselves use — see the note in CoachTabs.
      history.replace(coachTabPath(tabBasePath, tab));
      return;
    }
    setLocalTab(tab);
  };

  const bookSession = useSelector(
    (state: RootState) => state.bookSession,
    shallowEqual,
  );
  const facility = bookSession?.facility;

  // The coach the athlete just picked. Reading it from the booking state keeps
  // the header populated on the first paint; the fetch below fills in the rest.
  const selectedCoach: CoachProfileModel | undefined = useMemo(() => {
    const coaches: CoachProfileModel[] = facility?.coachProfiles || [];
    return (
      coaches.find((coach) => coach.externalId === coachExternalId) ||
      (bookSession?.coach?.externalId === coachExternalId
        ? bookSession.coach
        : undefined)
    );
  }, [facility, bookSession?.coach, coachExternalId]);

  const { self: user } = useSelector(
    (state: RootState) => state.user,
    shallowEqual,
  );
  const isAthlete = user?.kind === "athlete" || user?.kind === "other";
  const athleteSignedIn = Boolean(user?.id) && isAthlete;

  /**
   * CFTB-3437's sibling: this page's Clinics section asks the *public* programmes
   * index, which looks a facility up by uuid, and `facility.id` is its external id —
   * so that request 404d and the section was always empty. The facility calendar's
   * copy of this mistake was the one reported.
   *
   * Falls back to `id` where no uuid is known, which is the payload the in-app screen
   * holds; that request 404s as it always has rather than stopping.
   */
  /**
   * Whether the facility's cover is above this page (Patrick, 27 Aug, on frame
   * 526:50845). With one, the coach's tile straddles the foot of the picture and
   * carries no card and no rule of its own — the picture is the page's head, and a
   * line across it or a white box under it both read as a second one.
   */
  const overBanner =
    isPublicPage && hasFacilityBanner(facility?.public_cover_url);

  const { sessions, lessonRates, packages, events, profile, loading } =
    useCoachDetail(
      coachExternalId,
      facility?.uuid || facility?.id,
      athleteSignedIn,
    );

  /**
   * The coach's bio, contact details and cover photo, which `landing_page_coach`
   * does not carry — it answers with booking data. The same second request the
   * landing page makes, so both show the coach the same way.
   *
   * Asked for only where it is drawn: the public mount sits under the facility's
   * own banner and header strip (frame 526:50845), which carry all of this
   * already, so it asks for nothing.
   */
  const { orgProfile } = useCoachOrgProfile(
    isPublicPage ? undefined : coachExternalId,
    // In-app is the only mount that asks at all, and it is signed in — so the
    // authenticated record is the one to read (a coach who has published no
    // landing page has no public one).
    Boolean(user?.id),
  );

  const { credits: packageCredits } = usePackageCredits(
    athleteSignedIn,
    bookSession?.participantAthleteId,
  );


  // Same order as the booking page and the coach's calendar (#31): options a
  // held package covers come first, then the coach's own order, then the ones
  // that need a package the athlete does not have.
  // The caller's list wins where it gave one — see `lessonRates` on Props.
  const ratesToShow = lessonRatesProp ?? lessonRates;

  const orderedRates = useMemo(
    () => orderRatesForAthlete(ratesToShow, packageCredits),
    [ratesToShow, packageCredits],
  );

  // `sessions` is the landing-page payload, so a coach who has not switched their
  // landing page on has no descriptions here. The cards drop the line rather than
  // the session — title, duration and price all come from the rate itself.
  const descriptionFor = (rateId: number) =>
    sessions.find((session) => session.id === rateId)?.description;

  /**
   * The green "NEXT: …" line each session card carries in the frames.
   *
   * Per option, because availability is: a coach's next free 30 minutes is not
   * their next free 90, and an option can carry schedule limits of its own. The
   * lookup runs beside the page rather than in front of it, so the cards show their
   * title, price and button straight away and the line appears when it arrives — a
   * card whose option has nothing in the next two months keeps no line at all,
   * which is what the frame shows for one.
   */
  const { nextSlots } = useRateNextSlots(
    orderedRates,
    {
      datesId: selectedCoach?.id || profile?.id || coachExternalId,
      timesId: isPublicPage
        ? selectedCoach?.calendarUuid || profile?.calendarUuid
        : selectedCoach?.id || coachExternalId,
    },
    isPublicPage,
  );

  const nextAvailableFor = (rateId: number) => {
    const slot = nextSlots.get(rateId);
    if (!slot) {
      return undefined;
    }
    return nextSlotLabel(
      slot,
      DateTime.local(),
      {
        today: (time) => t("Next: Today {{time}}", { time }),
        tomorrow: (time) => t("Next: Tomorrow {{time}}", { time }),
        onDay: (day, time) => t("Next: {{day}} {{time}}", { day, time }),
      },
      timePattern(preferences?.timeFormat),
    );
  };

  const bookRate = (rate: LessonRateModel) => {
    dispatch(
      setBookSession({
        ...(selectedCoach ? { coach: selectedCoach } : {}),
        pricingValue: rate,
        selectedSession: rate,
        duration: rate.durationInMinutes,
      }),
    );
    // CFTB-3448: the session was chosen here, so the calendar it opens is asked for.
    // A `sessionId` that merely arrives in the URL speaks for nobody.
    history.push(bookRoute ? bookRoute(rate) : "/sessions/new", {
      sessionPicked: true,
    });
  };

  // The frame shows the coach's timezone under their name. The profile payload
  // does not always carry a display string, so fall back to resolving the
  // coach's (or the facility's) zone against the timezone list, which is how the
  // other booking screens label it.
  useTimeZonesModel();
  const timeZones = useSelector(
    (state: RootState) => state.schedulingTimeZones?.data || [],
  );
  const timezoneLabel =
    profile?.timeZoneDisplay ||
    timeZones.find(
      (zone: TimeZoneModel) =>
        zone.value === (selectedCoach?.timeZone || facility?.timeZone),
    )?.display ||
    "";

  // Package details live in a modal: the cards clamp their description to keep a
  // row even, and "Read more..." (or the card's own CTA) opens the full text.
  // Both the card and the modal are the public profile's, so the two surfaces
  // show packages identically.
  const [detailsPackage, setDetailsPackage] = useState<Package | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  const startPurchase = (pkg: Package) => {
    // CFTB-3445: the checkout answers 422 for a subscription already held, and the
    // handler it redirects to only logs that — leaving a spinner. The card's button
    // is inert in that state; this closes the modal's path to it too.
    if (isAlreadySubscribed(pkg)) {
      return;
    }
    setDetailsPackage(null);
    if (!canPurchaseOnline(pkg)) {
      setContactOpen(true);
      return;
    }
    // Already signed in here, so this skips the public flow's sign-in bridge and
    // goes straight to the checkout hand-off /post-login would have performed.
    history.push(
      `/post-login?coachId=${coachExternalId}&productId=${pkg.stripeProdId}`,
    );
  };

  // Switching coach without going back, which is what the chevron beside the
  // name does in the frame.
  const [coachMenuOpen, setCoachMenuOpen] = useState(false);
  const facilityCoaches: CoachProfileModel[] = facility?.coachProfiles || [];
  const otherCoaches = facilityCoaches.filter(
    (coach) => coach.externalId !== coachExternalId,
  );

  const seePackages = () => {
    // With the tabs in play the Overview holds only the first row of packages,
    // and the option that needs one may not be covered by any of them — so this
    // opens the tab that has all of them rather than scrolling to a sample.
    if (showTabs) {
      goToTab("packages");
      return;
    }
    // The packages are further down this same page.
    document
      .getElementById("coach-detail-packages")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const coachName =
    selectedCoach?.fullName ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");
  const metaLine = selectedCoach ? coachMetaLine(selectedCoach) : "";

  // Where the coach works, on the line under their title. The booking state has
  // the facility the athlete is booking at; opened from Home there is none, and
  // the coach's own facility is what the profile payload answers with.
  const placeName = facility?.name || profile?.facility?.name || "";

  /**
   * Phone, email and website as three buttons beside that line, which is where
   * the frame puts what the landing page gives a whole sidebar card to.
   *
   * All three are fields on the coach's own profile — Bio, Phone, Email, Website
   * — read through `useCoachOrgProfile`, which is the same request and the same
   * fields the landing page's sidebar was filled from. A coach who has not filled
   * them in has no buttons here, exactly as they had an empty sidebar card there.
   *
   * Phone and email fall back to the scheduling profile, which carries its own
   * pair: this page already trusts those for the Contact Coach modal, so without
   * the fallback a coach could be reachable through the modal and show nothing on
   * the line above it. `about` and `website` have no counterpart in that payload.
   *
   * The website is free text, so it goes through the same parse every other
   * coach-typed link on these screens does: http(s) only, and nothing that
   * resolves against our own origin.
   */
  const phone = orgProfile?.phone || profile?.phone;
  const email = orgProfile?.email || profile?.email;

  const contacts = [
    phone
      ? {
          key: "phone",
          icon: "ico-phone",
          href: `tel:${phone}`,
          label: t("Phone"),
          external: false,
        }
      : null,
    email
      ? {
          key: "email",
          icon: "ico-mail",
          href: `mailto:${email}`,
          label: t("Email"),
          external: false,
        }
      : null,
    safeExternalUrl(orgProfile?.website)
      ? {
          key: "website",
          icon: "ico-link",
          href: safeExternalUrl(orgProfile?.website) as string,
          label: t("Website"),
          external: true,
        }
      : null,
  ].filter(Boolean) as {
    key: string;
    icon: string;
    href: string;
    label: string;
    external: boolean;
  }[];

  /**
   * The cover photo the page draws its own banner from, with the coach's card
   * straddling its lower edge.
   *
   * The public mount draws none: the facility's banner is above this component
   * there, and `overBanner` below is how the card reaches back into it.
   */
  const coverImage = isPublicPage
    ? undefined
    : orgProfile?.coverUrl ||
      (hasFacilityBanner(facility?.public_cover_url)
        ? facility?.public_cover_url
        : undefined) ||
      (hasFacilityBanner(profile?.facility?.publicCoverUrl)
        ? profile?.facility?.publicCoverUrl
        : undefined);
  const ownBanner = Boolean(coverImage);
  const facilityLogo = hasFacilityLogo(facility?.public_logo_url)
    ? facility?.public_logo_url
    : facility?.logo_url || null;

  /**
   * The facility row — back arrow, logo, name — which the banner replaces: with a
   * cover photo the frame carries the back arrow on the picture and names the
   * facility on the card's own location line instead.
   *
   * Where the row does draw, that line would only repeat the name it already
   * carries in 22px type, so the location drops out of the card there.
   */
  const facilityRowVisible = showFacilityRow && !ownBanner;
  const showPlace = placeName !== "" && !facilityRowVisible;

  /**
   * The tabs belong to the coach's own page. The mount inside the facility
   * booking flow is one screen of that flow, which lists everything it has —
   * frame 526:50845 draws no tabs there.
   */
  const showTabs = !isPublicPage;
  const onOverview = !showTabs || activeTab === "overview";
  const showSessionsSection = onOverview || activeTab === "private";
  const showPackagesSection = onOverview || activeTab === "packages";
  const showEventsSection = onOverview || activeTab === "events";

  // Capped on the Overview and nowhere else — a section's own tab is where the
  // whole list is, which is what "View All" goes to.
  const capped = showTabs && activeTab === "overview";
  const shownRates = capped
    ? orderedRates.slice(0, OVERVIEW_SESSIONS)
    : orderedRates;
  const shownPackages = capped ? packages.slice(0, OVERVIEW_PACKAGES) : packages;
  const shownEvents = capped ? events.slice(0, OVERVIEW_EVENTS) : events;

  /**
   * Nothing to show *on this tab*: the Overview asks it of all three sections,
   * and a single tab of its own one. Without the second half, an athlete on the
   * Packages tab of a coach with no packages saw the heading and then nothing.
   */
  const nothingToShow =
    (!showSessionsSection || orderedRates.length === 0) &&
    (!showPackagesSection || packages.length === 0) &&
    (!showEventsSection || events.length === 0);

  return (
    // `sessions-page-container` is how a screen tells SiteWrapper to drop its
    // 820px cap — the same hook the other session pages use.
    //
    // Note what is NOT here: `public-profiles-wrapper`. That class scopes the
    // whole public-profile stylesheet, and wrapping the page in it put every
    // card on this page at the mercy of rules written for another one — which is
    // why the session cards kept drifting. Only the Clinics section, whose
    // component needs those styles, carries it now.
    <div className="sessions-page-container">
      <div
        className={cls(
          styles.container,
          overBanner && styles.container_over_banner,
          ownBanner && styles.container_own_banner,
        )}
      >
        {/* The coach's cover photo, full width, with the card below reaching back
            into it (frame 43592:26426). The back arrow rides on the picture — the
            frame keeps no facility row above it, since the facility is named on
            the card's own location line. */}
        {ownBanner && (
          <div className={styles.banner} data-testid="coach-banner">
            <img src={coverImage} alt="" className={styles.banner_image} />
            <button
              type="button"
              className={cls(styles.back_button, styles.back_button_on_banner)}
              onClick={() => history.goBack()}
              aria-label={t("Back")}
              data-testid="coach-detail-back"
            >
              <i className="ico ico-back" />
            </button>
          </div>
        )}
        {/* CFTB-3458: the rule under the facility name. Only where that name is in
            the public header strip above — the in-app page draws its own facility
            row below, and rules it off itself. */}
        {isPublicPage && !showFacilityRow && !overBanner && (
          <div className={styles.top_divider} />
        )}

        {facilityRowVisible && (
          <div className={styles.facility_row}>
            <button
              type="button"
              className={styles.back_button}
              onClick={() => history.goBack()}
              aria-label={t("Back")}
              data-testid="coach-detail-back"
            >
              <i className="ico ico-back" />
            </button>
            {facilityLogo && (
              <img className={styles.facility_logo} src={facilityLogo} alt="" />
            )}
            <span className={styles.facility_name}>{facility?.name}</span>
          </div>
        )}

        <div
          className={cls(
            styles.coach_card,
            overBanner && styles.coach_card_over_banner,
            ownBanner && styles.coach_card_on_banner,
          )}
        >
          <CoachAvatar
            // Either payload can be the one that arrived; the page draws whichever
            // has a picture, and their initials until one does.
            coach={
              {
                ...(profile || {}),
                ...(selectedCoach || {}),
                profilePicture:
                  selectedCoach?.profilePicture || profile?.profilePicture,
                fullName: coachName,
              } as any
            }
            className={styles.coach_avatar}
            // A third of the box, which is 100px on the card and 110 over a
            // banner.
            fontSize={32}
          />
          <div className={styles.coach_menu_wrapper}>
            <div className={styles.coach_name_row}>
              <div className={styles.coach_name}>{coachName}</div>
              {otherCoaches.length > 0 && (
                <button
                  type="button"
                  className={
                    coachMenuOpen
                      ? `${styles.coach_switcher} ${styles.coach_switcher_open}`
                      : styles.coach_switcher
                  }
                  onClick={() => setCoachMenuOpen((open) => !open)}
                  aria-label={t("Coach")}
                  aria-expanded={coachMenuOpen}
                  data-testid="coach-detail-switch"
                >
                  <i className="ico ico-chevron" />
                </button>
              )}
            </div>
            {metaLine !== "" && <div className={styles.coach_meta}>{metaLine}</div>}
            {timezoneLabel !== "" && (
              <div className={styles.coach_timezone}>
                <i className="ico ico-earth" />
                <span>{timezoneLabel}</span>
              </div>
            )}

            {/* Where they coach, and the three ways to reach them: the frame's
                one line in place of the landing page's whole sidebar card. */}
            {(showPlace || contacts.length > 0) && (
              <div className={styles.coach_place_row}>
                {showPlace && (
                  <span className={styles.coach_place} data-testid="coach-place">
                    <i className="ico ico-map-marker" />
                    <span>{placeName}</span>
                  </span>
                )}
                {contacts.map((contact) => (
                  <a
                    key={contact.key}
                    className={styles.coach_contact}
                    href={contact.href}
                    aria-label={contact.label}
                    title={contact.label}
                    {...(contact.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    data-testid={`coach-contact-${contact.key}`}
                  >
                    <i className={`ico ${contact.icon}`} />
                  </a>
                ))}
              </div>
            )}

            {coachMenuOpen && (
              <div className={styles.coach_menu}>
                {otherCoaches.map((coach) => (
                  <button
                    key={coach.id}
                    type="button"
                    className={styles.coach_menu_item}
                    onClick={() => {
                      setCoachMenuOpen(false);
                      history.push(`/sessions/coach/${coach.externalId}`);
                    }}
                  >
                    {coach.fullName}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Renders nothing for a coach who has written no bio. */}
        <CoachAbout text={orgProfile?.about} />

        {showTabs && (
          <CoachTabs
            active={activeTab}
            basePath={tabBasePath}
            onSelect={setLocalTab}
          />
        )}

        {loading ? (
          <div className={styles.loading_block}>
            <Loading isLoading loadType="spinner" />
          </div>
        ) : (
          <div className={styles.sections}>
            {showSessionsSection && orderedRates.length > 0 && (
              <section>
                <div className={styles.section_head}>
                  <h2 className={styles.section_title}>
                    {t("Private Sessions")}
                  </h2>
                  {shownRates.length < orderedRates.length && (
                    <button
                      type="button"
                      className={styles.view_all}
                      onClick={() => goToTab("private")}
                      data-testid="coach-view-all-private"
                    >
                      {t("View All")}
                    </button>
                  )}
                </div>
                <div className={styles.session_grid}>
                  {shownRates.map((rate) => (
                    <SessionCard
                      key={rate.id}
                      rate={rate}
                      description={descriptionFor(rate.id)}
                      nextAvailable={nextAvailableFor(rate.id)}
                      currencySymbol={selectedCoach?.currencySymbol}
                      packageCredit={getApplicablePackageCredit(
                        rate,
                        packageCredits,
                      )}
                      requiresPackage={rateRequiresPackage(rate, packageCredits)}
                      onBook={bookRate}
                      onSeePackages={seePackages}
                    />
                  ))}
                </div>
              </section>
            )}

            {showPackagesSection && packages.length > 0 && (
              <section id="coach-detail-packages">
                <div className={styles.section_head}>
                  <h2 className={styles.section_title}>
                    {t("Packages & Subscriptions")}
                  </h2>
                  {shownPackages.length < packages.length && (
                    <button
                      type="button"
                      className={styles.view_all}
                      onClick={() => goToTab("packages")}
                      data-testid="coach-view-all-packages"
                    >
                      {t("View All")}
                    </button>
                  )}
                </div>
                <div className={styles.package_grid}>
                  {shownPackages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      onOpenDetails={() => setDetailsPackage(pkg)}
                    />
                  ))}
                </div>
              </section>
            )}

            {showEventsSection && events.length > 0 && (
              <section>
                <div className={styles.section_head}>
                  <h2 className={styles.section_title}>
                    {t("Events & Clinics")}
                  </h2>
                  {shownEvents.length < events.length && (
                    <button
                      type="button"
                      className={styles.view_all}
                      onClick={() => goToTab("events")}
                      data-testid="coach-view-all-events"
                    >
                      {t("View All")}
                    </button>
                  )}
                </div>
                {/*
                  Scoped here: this component styles itself through the `pub-*`
                  rules, which only apply inside this class.

                  `showViewAll` is what that component calls its Overview mode —
                  its own heading, its own link, and a swipeable strip instead of
                  a grid. This page states the heading and the link itself, in the
                  same row the two sections above use, so the component is handed
                  the list it should draw and nothing else.
                */}
                <div className="public-profiles-wrapper">
                  <EventsClinics events={shownEvents} showViewAll={false} />
                </div>
              </section>
            )}

            {nothingToShow && (
              <div className={styles.empty_state}>
                {t("No available booking options")}
              </div>
            )}
          </div>
        )}
      </div>

      {detailsPackage && (
        <PackageDetailsModal
          product={detailsPackage as any}
          isOpen
          onClose={() => setDetailsPackage(null)}
          buttonTitle={t(packageCtaKey(detailsPackage))}
          buttonDisabled={isAlreadySubscribed(detailsPackage)}
          onButtonClick={() => startPurchase(detailsPackage)}
        />
      )}

      {contactOpen && (
        <ContactCoachModal
          isOpen
          onClose={() => setContactOpen(false)}
          coachName={coachName}
          phone={phone}
          email={email}
        />
      )}
    </div>
  );
};

export default CoachDetail;
